import { useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  DatePicker,
  TimePicker,
  Switch,
  Button,
  Space,
} from 'antd';

import './index.scss';
import { Promotion } from '@/types';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;

interface Props {
  onCancel: () => void;
  onSave: (values: any) => void;
  data: Promotion | null;
}

const PromotionForm = ({ onCancel, onSave, data }: Props) => {
  const [form] = Form.useForm();

  const handleSave = () => {
    form
      .validateFields()
      .then((values) => {
        if (onSave) {
          const startDateTime = values.startDate
            ? dayjs(values.startDate)
                .hour(values.startTime?.hour())
                .minute(values.startTime?.minute())
                .second(0)
                .toDate()
            : null;

          const endDateTime = values.endDate
            ? dayjs(values.endDate)
                .hour(values.endTime?.hour())
                .minute(values.endTime?.minute())
                .second(0)
                .toDate()
            : null;

          const formatData = {
            ...values,
            startDate: startDateTime,
            endDate: endDateTime,
            status: values.status ? 'active' : 'inactive',
            effect: values.effect.toLowerCase(),
            affected: values.affected.toLowerCase(),
          };

          onSave(formatData);
        }
      })
      .catch((info) => {
        console.log('Validation Failed:', info);
      });
  };

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        name: data.name,
        description: data.description,
        effect: data.effect,
        affected: data.affected,
        startDate: data.startDate ? dayjs(data.startDate) : null,
        endDate: data.endDate ? dayjs(data.endDate) : null,
        startTime: data.startDate ? dayjs(data.startDate) : null,
        endTime: data.endDate ? dayjs(data.endDate) : null,
        status: data.status === 'active',
      });
    }
  }, [data]);

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        status: true,
        effect: 'Price',
        affected: 'All',
      }}
      className="promotion-form"
    >
      {/* Name */}
      <Form.Item
        label="Name"
        name="name"
        rules={[
          { required: true, message: 'Please enter the promotion name!' },
        ]}
      >
        <Input placeholder="Enter promotion name" className="promotion-input" />
      </Form.Item>

      {/* Description */}
      <Form.Item label="Description" name="description">
        <TextArea
          placeholder="Enter description"
          rows={3}
          maxLength={250}
          className="promotion-input"
        />
      </Form.Item>

      <Space
        size="small"
        style={{
          display: 'flex',
          marginBottom: 16,
          justifyContent: 'start',
          width: '100%',
        }}
      >
        {/* Effect */}
        <Form.Item
          label="Effect"
          name="effect"
          style={{ flex: 1 }}
          rules={[{ required: true, message: 'Please select an effect!' }]}
        >
          <Select
            defaultValue="Price"
            className="promotion-input"
            style={{ width: '200px' }}
          >
            <Option value="Price">Price</Option>
            <Option value="Gift">Gift</Option>
          </Select>
        </Form.Item>

        {/* Affected */}
        <Form.Item
          label="Affected"
          name="affected"
          style={{ flex: 1 }}
          rules={[
            { required: true, message: 'Please select an affected scope!' },
          ]}
        >
          <Select
            defaultValue="All"
            className="promotion-input"
            style={{ width: '200px' }}
          >
            <Option value="All">All</Option>
            <Option value="Franchisor">Franchisor</Option>
            <Option value="Consultant">Consultant</Option>
          </Select>
        </Form.Item>
      </Space>

      <Space
        size="small"
        style={{ display: 'flex', marginBottom: 16, justifyContent: 'start' }}
      >
        {/* Starting Date */}
        <Form.Item
          label="Starting Date"
          name="startDate"
          style={{ flex: 1 }}
          rules={[
            { required: true, message: 'Please select a starting date!' },
          ]}
        >
          <DatePicker style={{ width: '200px' }} className="promotion-input" />
        </Form.Item>

        {/* Starting Time */}
        <Form.Item
          label="Starting Time"
          name="startTime"
          style={{ flex: 1 }}
          rules={[
            { required: true, message: 'Please select a starting time!' },
          ]}
        >
          <TimePicker
            use12Hours
            format="h:mm A"
            style={{ width: '200px' }}
            className="promotion-input"
          />
        </Form.Item>
      </Space>

      <Space
        size="small"
        style={{ display: 'flex', marginBottom: 16, justifyContent: 'start' }}
      >
        {/* End Date */}
        <Form.Item
          label="End Date"
          name="endDate"
          style={{ flex: 1 }}
          rules={[{ required: true, message: 'Please select an end date!' }]}
        >
          <DatePicker style={{ width: '200px' }} className="promotion-input" />
        </Form.Item>

        {/* End Time */}
        <Form.Item
          label="End Time"
          name="endTime"
          style={{ flex: 1 }}
          rules={[{ required: true, message: 'Please select an end time!' }]}
        >
          <TimePicker
            use12Hours
            format="h:mm A"
            style={{ width: '200px' }}
            className="promotion-input"
          />
        </Form.Item>
      </Space>

      {/* Status */}
      <Form.Item label="Status" name="status" valuePropName="checked">
        <Switch checkedChildren="Running" unCheckedChildren="Paused" />
      </Form.Item>

      {/* Footer Buttons */}
      <Form.Item>
        <Space style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Button
            type="primary"
            className={'promotion-add-modal-button'}
            onClick={handleSave}
          >
            Save Changes
          </Button>
          <Button className={'promotion-cancel-button'} onClick={onCancel}>
            Cancel
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default PromotionForm;
